'use es6';

import { List } from 'immutable';
import { getDatasetInsightsMetricName, getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
import { AccumulateAlong, AccumulateTransform } from 'reporting-visualizations/transforms/transformRecords';
import { Axis, XYVisualization } from 'reporting-visualizations/visualizations/xy/xyRecords';
import { getDataLabelsStyle, getStackLabelStyle } from '../../../shared/lib/style';
import { toVisualizationOrder } from '../../modify/sorts/report-sort-utils';
import { getXTotalColumn } from '../../modify/totals/xyTotals';
import { GOAL_ID, reportSupportsGoals } from '../../resolve/goal';
import { getDateResolution, getReportColorEncoding, getReportXEncoding, getReportXMultiEncoding, getReportYEncoding } from '../../utils/report-utils';
import { getEncodingColumnAlias, getEncodingFormatParameters, getEncodingFormatType } from '../../utils/visual-utils';
import { applyScaleToAxis } from '../apply-scale-to-axis';
import { applyTypeToAxis } from '../apply-type-to-axis';
import { doMeasuresHaveSameFormattingType, getGoalTransform, getMeasureAxisLabel, getMultiMeasureFormatting, processReportStacking, toXYColor } from './shared';
import { findDatasetInsights } from 'reporting-data/datasetInsights';
import { buildXYInsightLayerOverrides } from 'reporting-visualizations/visualizations/xy/xyUtils';
import CssVariablesTheme from 'foundations-theming/css-vars-theme';
const getGoalLayer = (report, dataset) => {
  const {
    accumulate
  } = report.visual.options;
  const y = getReportYEncoding(report);
  const xAxisKey = 'bottom';
  const color = getReportColorEncoding(report);
  const yField = y && getEncodingColumnAlias(y);
  const accumulateAlong = AccumulateAlong({
    field: yField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49569/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  return {
    dataset: 'primary',
    mark: {
      type: 'line',
      zIndex: 10,
      marker: {
        enabled: true
      }
    },
    x: {
      type: 'linear',
      field: GOAL_ID,
      blend: xAxisKey,
      fill: true
    },
    y: y ? {
      type: 'category',
      field: yField,
      blend: 'left',
      order: toVisualizationOrder(y.column, dataset)
    } : undefined,
    color: {
      default: CssVariablesTheme['color']['do-not-use-battleship']
    },
    transform: getGoalTransform(yField, color, accumulate, accumulateAlong),
    excludeFromLegacyTooltipTotals: true
  };
};
const mapSingleMeasureEncodingVisual = (report, datasets, brandKitPalettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const insightDatasets = findDatasetInsights(datasets);
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  const x = getReportXEncoding(report);
  const y = getReportYEncoding(report);
  const color = getReportColorEncoding(report);
  const visualOptions = report.visual.options;
  const {
    showDataLabels,
    stacking: stackingOption,
    stackingType,
    xMinMax,
    xAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate
  } = visualOptions;
  const stacking = processReportStacking(stackingOption, stackingType);
  const xAxisKey = 'bottom';
  const yField = y && getEncodingColumnAlias(y);
  const xField = x && getEncodingColumnAlias(x);
  const colorField = color && getEncodingColumnAlias(color);
  const maybeDateResolution = getDateResolution(yField, report);
  const accumulateAlong = AccumulateAlong({
    field: yField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49569/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const primaryLayer = {
    dataset: 'primary',
    mark: {
      type: 'bar',
      stacking
    },
    x: x ? {
      type: 'linear',
      field: xField,
      blend: xAxisKey,
      fill: true,
      formattingType: getEncodingFormatType(x),
      formattingObject: getEncodingFormatParameters(x)
    } : {
      type: 'scaffold',
      blend: xAxisKey
    },
    y: y ? {
      type: 'category',
      field: yField,
      blend: 'left',
      order: toVisualizationOrder(y.column, primaryDataset),
      formattingType: getEncodingFormatType(y),
      formattingObject: getEncodingFormatParameters(y)
    } : {
      type: 'scaffold',
      blend: 'left'
    },
    color: toXYColor(color && color.column, x && x.column && List.of(x.column), primaryDataset, colorOption, color, brandKitPalettes),
    transform: accumulate ? [AccumulateTransform({
      groupBy: [colorField],
      field: xField,
      along: accumulateAlong
    })] : []
  };
  const insightsLayers = hasInsightDatasets ? insightDatasets.reduce((result, insightDataset, key) => {
    const insightLayerOverrides = buildXYInsightLayerOverrides(insightDataset, key, visualOptions);
    result.push(Object.assign({}, primaryLayer, insightLayerOverrides.primary));
    return result;
  }, []) : [];
  const goalLayers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(report, primaryDataset)] : [];
  const layers = [...goalLayers, primaryLayer, ...insightsLayers];
  return XYVisualization({
    type: 'XY',
    data: datasets,
    style: {
      dataLabels: getDataLabelsStyle(stacking, showDataLabels),
      stackLabels: getStackLabelStyle(stacking, showDataLabels),
      legendPosition,
      legendWidth,
      x: {
        [xAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), xMinMax), xAxisType)
      },
      dateResolution: maybeDateResolution
    },
    layers
  });
};
const mapMultiMeasureEncodingVisual = (report, datasets, brandKitPalettes) => {
  const primaryDataset = getPrimaryDataset(datasets);
  const insightDatasets = findDatasetInsights(datasets);
  const hasInsightDatasets = insightDatasets && insightDatasets.count();
  const xMulti = getReportXMultiEncoding(report);
  const y = getReportYEncoding(report);
  const color = getReportColorEncoding(report);
  const visualOptions = report.visual.options;
  const {
    showDataLabels,
    stacking: stackingOption,
    stackingType,
    xMinMax,
    xAxisType,
    color: colorOption,
    goalLineValue,
    legendPosition,
    legendWidth,
    accumulate,
    xAxisLabel: labelSetting
  } = visualOptions;
  const stacking = processReportStacking(stackingOption, stackingType);
  const xAxisLabel = getMeasureAxisLabel(labelSetting, xMulti, primaryDataset);
  const xAxisKey = 'bottom';
  const yField = y && getEncodingColumnAlias(y);
  const colorField = color && getEncodingColumnAlias(color);
  const maybeDateResolution = getDateResolution(yField, report);
  const accumulateAlong = AccumulateAlong({
    field: yField,
    /*
    TODO: We only allow ascending accumulations due to
     reporting-snowflake/static-1.49569/js/relational/validate/constraints/visuals/check-sort-is-accumulate-friendly.js:4
     but there is no longer a technical reason why we can't support descending accumulations now.
      We should update this in the near future.
      For now, it is okay to hardcode this
    */
    direction: 'ascending'
  });
  const allMeasuresHaveSameFormattingType = doMeasuresHaveSameFormattingType(xMulti, primaryDataset);
  const formattingObjectMap = getMultiMeasureFormatting(xMulti);
  const useMultiEncodingFormattingObject = xMulti.size > 1;
  const getAxisTotal = () => {
    const xTotal = getXTotalColumn(report);
    return xTotal && xTotal.alias;
  };
  const buildYMultiPrimaryLayer = (xEncoding, xField) => {
    return {
      dataset: 'primary',
      mark: {
        type: 'bar',
        stacking
      },
      x: {
        type: 'linear',
        field: xField,
        blend: xAxisKey,
        fill: true,
        formattingObjectMap: useMultiEncodingFormattingObject ? formattingObjectMap : undefined,
        formattingType: allMeasuresHaveSameFormattingType ? getEncodingFormatType(xEncoding) : undefined,
        formattingObject: allMeasuresHaveSameFormattingType ? getEncodingFormatParameters(xEncoding) : undefined
      },
      y: y ? {
        type: 'category',
        field: yField,
        blend: 'left',
        order: toVisualizationOrder(y.column, primaryDataset),
        formattingType: getEncodingFormatType(y),
        formattingObject: getEncodingFormatParameters(y)
      } : {
        type: 'scaffold',
        blend: 'left'
      },
      color: toXYColor(color && color.column, xMulti.map(encoding => encoding.column), primaryDataset, colorOption, xMulti.size > 1 ? xEncoding : color, brandKitPalettes),
      transform: accumulate ? [AccumulateTransform({
        groupBy: [colorField],
        field: xField,
        along: accumulateAlong
      })] : [],
      axisTotal: getAxisTotal()
    };
  };
  const primaryLayers = xMulti.map(xEncoding => {
    const xField = getEncodingColumnAlias(xEncoding);
    return buildYMultiPrimaryLayer(xEncoding, xField);
  });
  const insightsLayers = hasInsightDatasets ? insightDatasets.reduce((result, insightDataset, key) => {
    const insightLayerOverrides = buildXYInsightLayerOverrides(insightDataset, key, visualOptions);
    const xField = getDatasetInsightsMetricName(insightDataset);
    const xEncoding = xMulti.find(encoding => encoding.column.alias === xField);
    const primaryLayer = buildYMultiPrimaryLayer(xEncoding, xField);
    result.push(Object.assign({}, primaryLayer, insightLayerOverrides.primary));
    return result;
  }, []) : [];
  const goalLayers = goalLineValue && reportSupportsGoals(report) ? [getGoalLayer(report, primaryDataset)] : [];
  const layers = [...goalLayers, ...primaryLayers, ...insightsLayers];
  return XYVisualization({
    type: 'XY',
    data: datasets,
    style: {
      dataLabels: getDataLabelsStyle(stacking, showDataLabels),
      stackLabels: getStackLabelStyle(stacking, showDataLabels),
      legendPosition,
      legendWidth,
      showTotals: allMeasuresHaveSameFormattingType,
      x: {
        [xAxisKey]: applyTypeToAxis(applyScaleToAxis(Axis({}), xMinMax), xAxisType).set('title', xAxisLabel)
      },
      dateResolution: maybeDateResolution
    },
    layers
  });
};
export const mapHorizontalBarVisual = (report, datasets, __options, {
  brandKitPalettes
}) => {
  const xMulti = getReportXMultiEncoding(report);
  if (xMulti && xMulti.size > 0) {
    return mapMultiMeasureEncodingVisual(report, datasets, brandKitPalettes);
  }
  return mapSingleMeasureEncodingVisual(report, datasets, brandKitPalettes);
};
export const __TESTABLE__ = {
  mapMultiMeasureEncodingVisual,
  mapSingleMeasureEncodingVisual
};